import type { Employee } from '@smartphone-tycoon/shared-core';

/**
 * Calculate RP generation from researchers using logarithmic scaling
 * @param researchers - Array of employees with researcher role
 * @param monthlyInvestment - Monthly research budget investment
 * @returns Daily RP generation from researchers
 */
export function calculateResearcherRP(
  researchers: Employee[],
  monthlyInvestment: number
): number {
  const researcherCount = researchers.length;
  
  // Minimum 10 researchers needed to generate RP
  if (researcherCount < 10) {
    return 0;
  }
  
  // Base RP generation using logarithmic scaling
  // log10(10) = 1, log10(100) = 2, log10(1000) = 3
  const logarithmicFactor = Math.log10(researcherCount);
  
  // Base RP per day (scaled by logarithmic factor)
  const baseRPPerDay = 10 * logarithmicFactor;
  
  // Calculate investment per researcher (daily)
  const dailyInvestment = monthlyInvestment / 30;
  const investmentPerResearcher = researcherCount > 0 ? dailyInvestment / researcherCount : 0;
  
  // Optimal investment per researcher (daily equivalent of $50k/year)
  const optimalInvestmentPerResearcher = 50000 / 365;
  
  // Calculate efficiency based on investment/researcher ratio
  // Efficiency peaks at optimal investment and decreases both above and below
  let efficiency = 1;
  if (investmentPerResearcher > 0) {
    const ratio = investmentPerResearcher / optimalInvestmentPerResearcher;
    
    if (ratio < 1) {
      // Under-investment: linear decrease in efficiency
      efficiency = 0.3 + (0.7 * ratio);
    } else if (ratio > 1) {
      // Over-investment: diminishing returns using logarithmic decay
      efficiency = 1 + (0.3 * Math.log10(ratio) / Math.log10(10));
      efficiency = Math.min(efficiency, 1.5); // Cap at 150% efficiency
    }
  }
  
  // Factor in average researcher performance and skills
  const avgPerformance = researchers.reduce((sum, r) => sum + r.performance, 0) / researcherCount;
  const avgTechnicalSkill = researchers.reduce((sum, r) => sum + (r.skills.technical || 0), 0) / researcherCount;
  
  // Performance modifier (0.5 to 1.5)
  const performanceModifier = 0.5 + (avgPerformance / 100);
  
  // Skill modifier (0.8 to 1.2 based on technical skill)
  const skillModifier = 0.8 + (avgTechnicalSkill / 100 * 0.4);
  
  // Calculate final daily RP
  const dailyRP = baseRPPerDay * efficiency * performanceModifier * skillModifier;
  
  return Math.round(dailyRP * 100) / 100; // Round to 2 decimal places
}

/**
 * Get a breakdown of RP generation factors for UI display
 */
export function getResearcherRPBreakdown(
  researchers: Employee[],
  monthlyInvestment: number
): {
  researcherCount: number;
  baseRP: number;
  efficiency: number;
  performanceModifier: number;
  skillModifier: number;
  totalDaily: number;
  investmentPerResearcher: number;
  optimalInvestmentPerResearcher: number;
} {
  const researcherCount = researchers.length;
  
  if (researcherCount < 10) {
    return {
      researcherCount,
      baseRP: 0,
      efficiency: 0,
      performanceModifier: 0,
      skillModifier: 0,
      totalDaily: 0,
      investmentPerResearcher: 0,
      optimalInvestmentPerResearcher: 50000 / 365
    };
  }
  
  const logarithmicFactor = Math.log10(researcherCount);
  const baseRP = 10 * logarithmicFactor;
  
  const dailyInvestment = monthlyInvestment / 30;
  const investmentPerResearcher = dailyInvestment / researcherCount;
  const optimalInvestmentPerResearcher = 50000 / 365;
  
  let efficiency = 1;
  if (investmentPerResearcher > 0) {
    const ratio = investmentPerResearcher / optimalInvestmentPerResearcher;
    
    if (ratio < 1) {
      efficiency = 0.3 + (0.7 * ratio);
    } else if (ratio > 1) {
      efficiency = 1 + (0.3 * Math.log10(ratio) / Math.log10(10));
      efficiency = Math.min(efficiency, 1.5);
    }
  }
  
  const avgPerformance = researchers.reduce((sum, r) => sum + r.performance, 0) / researcherCount;
  const avgTechnicalSkill = researchers.reduce((sum, r) => sum + (r.skills.technical || 0), 0) / researcherCount;
  
  const performanceModifier = 0.5 + (avgPerformance / 100);
  const skillModifier = 0.8 + (avgTechnicalSkill / 100 * 0.4);
  
  const totalDaily = baseRP * efficiency * performanceModifier * skillModifier;
  
  return {
    researcherCount,
    baseRP: Math.round(baseRP * 100) / 100,
    efficiency: Math.round(efficiency * 100) / 100,
    performanceModifier: Math.round(performanceModifier * 100) / 100,
    skillModifier: Math.round(skillModifier * 100) / 100,
    totalDaily: Math.round(totalDaily * 100) / 100,
    investmentPerResearcher: Math.round(investmentPerResearcher),
    optimalInvestmentPerResearcher: Math.round(optimalInvestmentPerResearcher)
  };
}